/**
 * @bos/portal3 — ONE STAGE'S ROOM (`/app/runs/:id/plan/lifecycles`, `…/rules`, …).
 *
 * The six later stage reviews, each at its own address: the artifact in the owner's
 * language, the approval door bound to the exact bytes, the disagree door with its cost
 * said before the click, the exact-edit composer where the stage's bytes carry one, and
 * the modules room's honest no-door (ProseOnlyNote, kept verbatim — it is one of the best
 * paragraphs in the product).
 *
 * AND, ABOVE ALL OF IT, WHAT THE MACHINE ASKED ABOUT THIS. A question carrying this stage's
 * name renders here now, with the thing it is about underneath it — the owner is no longer
 * asked to decide something they cannot see. The answering is unchanged in every particular;
 * only the address moved.
 *
 * Everything else here is <StageReview> exactly as it was; the room only decides WHICH stage,
 * and the read is still lazy per address — opening Rules fetches Rules and nothing else.
 */
import { useStageReview } from '../hooks';
import { StageReview } from '../StageReview';
import { RoomQuestions } from '../RoomQuestions';
import { useRunRoom } from '../roomContext';
import { roomForStage, type StageReviewKey } from '../model';

export function StageRoom({ stage }: { stage: StageReviewKey }) {
  const { runId, ready, statusKey, view, rail, questions, submitAnswers, intake } = useRunRoom();
  const review = useStageReview(runId, ready ? stage : null, statusKey);

  // The same double gate the data model has: the journal says the run is waiting AND this
  // stage's own bytes say an approval is on offer.
  const doorOpen =
    view.status === 'awaiting-owner' &&
    review.door !== null &&
    (review.door.approval.state === 'awaiting' || review.door.approval.state === 'stale');

  const room = roomForStage(stage);
  const railKey = room?.railKey ?? stage;

  return (
    <>
      {room !== null && (
        <RoomQuestions
          room={room.slug}
          questions={questions}
          onSubmit={submitAnswers}
          intake={intake}
          // The strip's mid-derivation fallback turns on exactly when this room has no
          // artifact to stand the question beside — the read surface's own state, not a guess.
          artifactDerived={
            review.srStatus === 'ready' &&
            review.payload !== null &&
            review.payload.read.state === 'ready'
          }
          runClosed={view.status === 'completed'}
        />
      )}
      {/* KEYED ON THE STAGE: react-router renders <StageRoom stage=…/> at the SAME tree
          position for every /plan/* route, so without the key React would REUSE one
          StageReview instance across stages — and a lifecycles gesture, its reported
          basket, and a half-typed disagree draft would leak into the permissions door.
          The key makes a stage change a fresh mount, which is what it is. */}
      <StageReview
        key={stage}
        stage={stage}
        railState={rail.find((r) => r.key === railKey)?.state ?? null}
        payload={review.payload}
        status={review.srStatus === 'idle' ? 'loading' : review.srStatus}
        error={review.srError}
        door={review.door}
        doorOpen={doorOpen}
        onApprove={review.approve}
        onRequestRevision={review.requestRevision}
        onPreviewEdit={review.previewEdit}
        onOrderEdit={review.submitEdit}
        onPreviewBasket={review.previewBasket}
        onApplyBasket={review.submitBasket}
      />
    </>
  );
}
